// ============================================================
// store/stash-card-detail/hooks.ts
//
// Memoized selector hooks that wrap pipeline.ts builders. These are
// the public consumption surface for the detail page components.
// Mirrors `store/stash-scoped/hooks.ts`.
// ============================================================

import { useMemo } from 'react';
import { useStashCardDetailStore } from './context';
export { useStashCardDetailStore } from './context';
import {
  buildReachState,
  buildHistory,
  buildStashersStrip,
  buildFeaturedNote,
  buildCategoryBreadcrumb,
} from './pipeline';
import type {
  ReachState,
  HistoryItem,
  StasherChip,
  FeaturedNote,
} from './types';

/** The current Stash Card. */
export function useStashCard() {
  return useStashCardDetailStore(s => s.card);
}

/** The vendor on the current card (after overrides applied). */
export function useVendor() {
  const card = useStashCard();
  return useMemo(() => {
    if (!card) return null;
    return card.vendorOverrides ?? card.vendor;
  }, [card]);
}

/** Reach state — the contact pattern's primary + alt tiles. */
export function useReachState(opts?: {
  /** Pre-computed distance from user to vendor in miles (optional). */
  distanceMi?: number | null;
  /** Used in tests; defaults to real wall clock. */
  now?: Date;
}): ReachState | null {
  const card = useStashCard();
  const vendor = useVendor();
  return useMemo(() => {
    if (!card || !vendor) return null;
    const stasherCount = vendor.stashCardStasherIds?.length ?? 0;
    return buildReachState(card, vendor, stasherCount, opts?.distanceMi ?? null, opts?.now);
  }, [card, vendor, opts?.distanceMi, opts?.now]);
}

/** Reverse-chronological history (call · text · web · email · map · note). */
export function useHistory(): HistoryItem[] {
  const card = useStashCard();
  const vendor = useVendor();
  const stashers = useStashCardDetailStore(s => s.stashers);
  const currentUserId = useStashCardDetailStore(s => s.currentUserStasherId);
  return useMemo(() => {
    if (!card || !vendor) return [];
    return buildHistory(card, vendor, stashers, currentUserId);
  }, [card, vendor, stashers, currentUserId]);
}

/** The pulled-quote note + author resolved from the card's stasher. */
export function useFeaturedNote(): FeaturedNote | null {
  const card = useStashCard();
  const stashers = useStashCardDetailStore(s => s.stashers);
  const currentUserId = useStashCardDetailStore(s => s.currentUserStasherId);
  return useMemo(() => {
    if (!card) return null;
    return buildFeaturedNote(card, stashers, currentUserId)?.note ?? null;
  }, [card, stashers, currentUserId]);
}

/**
 * Stashers strip — friends-first, current-user excluded. Pass the set of
 * friend ids so the strip can boost them to the front and tag them visually.
 */
export function useStashersStrip(friendIds: Set<string>): StasherChip[] {
  const vendor = useVendor();
  const stashers = useStashCardDetailStore(s => s.stashers);
  const currentUserId = useStashCardDetailStore(s => s.currentUserStasherId);
  const card = useStashCard();
  return useMemo(() => {
    if (!vendor || !card) return [];
    const featured = buildFeaturedNote(card, stashers, currentUserId);
    return buildStashersStrip(
      vendor,
      stashers,
      friendIds,
      currentUserId,
      featured?.authorStasherId ?? null,
    );
  }, [vendor, stashers, friendIds, currentUserId, card]);
}

/** Category breadcrumb for the page header. */
export function useCategoryBreadcrumb(): string[] {
  const card = useStashCard();
  const vendor = useVendor();
  const categories = useStashCardDetailStore(s => s.categories);
  return useMemo(() => {
    if (!card || !vendor || vendor.categories.length === 0) return [];
    // The vendor.categories[] is leaf-most first; use the first one.
    const leaf = vendor.categories[0];
    if (!leaf) return [];
    return buildCategoryBreadcrumb(leaf, categories);
  }, [card, vendor, categories]);
}

/** Action: log a new activity to the card. */
export function useLogActivity() {
  return useStashCardDetailStore(s => s.logActivity);
}

/** Action: add a user-authored note. */
export function useAddNote() {
  return useStashCardDetailStore(s => s.addNote);
}
